import { useContext, useEffect, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { MapContext } from '../../contexts/MapContext';
import { ItemContext } from '../../contexts/ItemContext';
import { getRowId } from '../../utils/field';
import { PanelSection } from '../SidePanel';
import { asQuantity, asTruncatedString } from '../../utils/formatters';
import { useUnits } from '../../contexts/UnitContext';
import { DISTANCE_UNITS } from '../../constants/units';

const RowItem = styled.div`
  margin: 0 -1rem;
  padding: 0 2rem;
  ${({ isActive }) =>
    isActive &&
    `
      background-color: var(--active-color);
      font-weight: bold;
    `}
`;

// Find the first item in the list that is fully visible
// within the scrolling container
const setFirstVisibleItemActive = (
  sectionId,
  itemsArrayRef,
  containerRef,
  setActiveKey,
  clearActiveKey,
) => () => {
  const {
    top: containerTop,
    bottom: containerBottom,
  } = containerRef.current.getBoundingClientRect();
  const firstVisibleItemIdx = itemsArrayRef.current.findIndex((itemEl) => {
    if (!itemEl) return false;
    const { top, bottom } = itemEl.getBoundingClientRect();
    return top >= containerTop && bottom <= containerBottom;
  });
  if (firstVisibleItemIdx >= 0) {
    setActiveKey(getRowId(sectionId, firstVisibleItemIdx));
  } else {
    clearActiveKey();
  }
};

const RowsList = ({ rows, containerRef, sectionId }) => {
  const { activeKey, setActiveKey, clearActiveKey } = useContext(ItemContext);
  const { displayUnit, transformToUnit } = useUnits(DISTANCE_UNITS);
  const {
    mapState: { selectRows },
  } = useContext(MapContext);
  const itemsArrayRef = useRef([]);
  const onScroll = useMemo(
    () =>
      setFirstVisibleItemActive(
        sectionId,
        itemsArrayRef,
        containerRef,
        setActiveKey,
        clearActiveKey,
      ),
    [sectionId, containerRef, setActiveKey, clearActiveKey],
  );

  useEffect(() => {
    const container = containerRef.current;
    container.addEventListener('scroll', onScroll);
    onScroll();
    return () => {
      clearActiveKey();
      container.removeEventListener('scroll', onScroll);
    };
  }, [clearActiveKey, containerRef, onScroll]);
  return (
    <PanelSection className="px-0 pt-0">
      {rows.map((row, i) => {
        const rowId = getRowId(sectionId, i);
        const { treeCount = 0, cropName = 'Unknown', rowLength = 0 } =
          row.properties || {};
        return (
          <RowItem
            role="button"
            key={rowId}
            className="text-center py-2 d-flex justify-content-between align-items-center"
            isActive={activeKey === rowId}
            ref={(element) => itemsArrayRef.current.push(element)}
            onClick={() => {
              clearActiveKey();
              // NOTE: we don't currently support editing within the mobile mode, so there's no multi-row editing here.
              selectRows({ sectionId, rowIndexes: [i] });
            }}
          >
            <div>Row {i + 1}</div>
            <div className="ml-2 font-tiny font-weight-normal text-muted">
              {treeCount > 0 && asQuantity(treeCount)}{' '}
              {asTruncatedString(cropName, 25)}
              {' • '}
              {asQuantity(transformToUnit(rowLength), {
                unit: displayUnit,
              })}
            </div>
          </RowItem>
        );
      })}
    </PanelSection>
  );
};

export default RowsList;
